/** Economy tuning — one place to balance the game. Mirrored to the client via /api/meta. */
import type { CardSet } from './cards.js';

export const ECON = {
  signup_credits: 200,
  daily_login: 25,
  invite_bonus: 100,
  invited_bonus: 50,
  pvp_win: 80,
  pvp_loss: 25,
  pack_price: 150,
  sunset_pack_price: 200,
  free_pack_hours: 12,
  free_pack_max: 2,
  goof_pick_price: 60,
  xp_win: 60,   // PvP; solo battles use AI_REWARDS
  xp_loss: 20,
  xp_pack: 10,
  trade_daily_cap: 5,
} as const;
export type Econ = typeof ECON;

/**
 * Solo battles against the AI, by difficulty: what a win pays, and the XP a loss costs. Giving up (or disconnecting)
 * costs the same as losing, so a bad game can't be dodged. XP never goes below 0.
 */
export const AI_REWARDS = {
  easy: { credits: 20, xp: 30, lossXp: -20 },
  normal: { credits: 50, xp: 60, lossXp: -50 },
  hard: { credits: 100, xp: 100, lossXp: -90 },
} as const;
export type Difficulty = keyof typeof AI_REWARDS;

/** XP needed for each rank. XP can go down (solo losses), so ranks can too. */
export const RANKS: ReadonlyArray<readonly [number, string]> = [
  [0, 'Poopy'], [300, 'Bronze'], [1000, 'Silver'], [2500, 'Gold'], [5000, 'Diamond'], [9000, 'Mythic'], [15000, 'Legendary'], [25000, 'Gooflord'],
];
export function rankFor(xp: number): string {
  let name: string = RANKS[0][1];
  for (const [t, n] of RANKS) if (xp >= t) name = n;
  return name;
}
/** The next rank up and the XP it needs, or null at the top. */
export function nextRank(xp: number): { name: string; at: number } | null {
  const next = RANKS.find(([t]) => t > xp);
  return next ? { name: next[1], at: next[0] } : null;
}

export const ROLES = ['user', 'champion', 'moderator', 'admin', 'super_admin'] as const;
export type Role = (typeof ROLES)[number];
export const roleAtLeast = (have: Role, need: Role) => ROLES.indexOf(have) >= ROLES.indexOf(need);

export interface MissionDef { id: string; name: string; stat: 'battles' | 'wins' | 'packs' | 'tames' | 'kills'; target: number; reward: number }
export const DEFAULT_MISSIONS: MissionDef[] = [
  { id: 'play1', name: 'Play a battle', stat: 'battles', target: 1, reward: 30 },
  { id: 'win1', name: 'Win a battle', stat: 'wins', target: 1, reward: 50 },
  { id: 'pack1', name: 'Open a pack', stat: 'packs', target: 1, reward: 20 },
  { id: 'tame5', name: 'Tame 5 wild fighters', stat: 'tames', target: 5, reward: 30 },
];

/**
 * Virtual packs: the set each one draws from and how many rare-or-better cards it guarantees (5 cards each). Prices
 * live in the store table. `reprints` are cards from another set that Ben reprinted in this one. A free pack opens as
 * any pack listed here, but a pair of free packs must be one of each (issue #28).
 */
export const PACKS: Record<string, { set: CardSet; guaranteed: number; name: string; reprints?: string[] }> = {
  'pack-standard': { set: 'og', guaranteed: 1, name: 'OG pack' },
  'pack-sunset': { set: 'sunset', guaranteed: 1, name: 'Sunset pack', reprints: ['booger-ghost'] },
};

/** Saved deck slots per player (issue #7). The active one is the deck used in battles. */
export const DECK_SLOTS = 4;
export interface DeckSlot { name: string; gooflord: string | null; fighters: string[]; items: string[] }

/** Deck legality — the same numbers on client (deck builder) and server (validation). */
export const DECK_RULES = { fightersMin: 12, fightersMax: 22, itemsMin: 6, itemsMax: 15, maxCopies: 1, corruptedMax: 2 } as const;

/** Emote stickers — the only "chat" in a battle. Safe for a young audience. */
export const EMOTES = [
  { id: 'gg', emoji: '🤝', label: 'Good game' },
  { id: 'lol', emoji: '😂', label: 'Ha!' },
  { id: 'oops', emoji: '🙈', label: 'Oops' },
  { id: 'wow', emoji: '🤯', label: 'Wow' },
  { id: 'think', emoji: '🤔', label: 'Thinking…' },
  { id: 'goof', emoji: '👑', label: 'Gooflord!' },
] as const;
export type EmoteId = (typeof EMOTES)[number]['id'];
